
import {
  Component, PropSync, Prop, Watch, Mixins,
} from 'vue-property-decorator';
import { InputValidationRules } from 'vuetify';
import { AddressBookItem } from '@/features/shipment/types';
import { updateAddressBookItem } from '@/features/shipment/services/addresses-book.services';
import { showErrorAlertDialog, showSuccessAlertDialog } from '@/features/common/helpers';
import {
  requiredRules,
  emailRules,
  minCharactersRules,
  phoneNumberInvalidRules,
  getPostalCodeRules,
  isProvinceRequired,
  stripUnoCharFromPostalCode,
} from '@/features/common/helpers/validationRules';
import { CountryInfo } from '@/features/common/types';
import CountrySelect from '@/features/common/components/CountrySelect.vue';
import { CityProvinceLookupMixin } from '../mixins';

@Component({
  components: {
    CountrySelect,
  },
})
export default class EditAddressDialog extends Mixins(CityProvinceLookupMixin) {
  @PropSync('show', { required: true }) syncedShow!: boolean;

  @Prop({ required: true }) addressItem!: AddressBookItem;

  private isFormValid = false;

  private isUpdating = false;

  private formData: AddressBookItem = {
    id: 0,
    nickname: '',
    company: '',
    contact: '',
    phone: '',
    addr1: '',
    addr2: '',
    addr3: '',
    countryCode: '',
    postalCode: '',
    province: '',
    city: '',
    residential: false,
    emails: [],
    taxId: '',
    isInside: false,
    isTailGate: false,
    isTradeShow: false,
    isLimitedAccess: false,
  };

  private requiredRules = requiredRules;

  private emailRules = emailRules;

  private minCharactersRules = minCharactersRules;

  private phoneNumberInvalidRules = phoneNumberInvalidRules;

  get emailValue(): string {
    return this.formData.emails && this.formData.emails[0] ? this.formData.emails[0] : '';
  }

  set emailValue(value: string) {
    this.formData.emails = value ? [value] : [];
  }

  get currentCountry(): CountryInfo | undefined {
    return this.$store.getters.getCountryFromCode(this.formData.countryCode);
  }

  get provinceDetails(): {
    rules: InputValidationRules | null,
    suffix: string,
    label: string,
    } {
    const required = isProvinceRequired(this.formData.countryCode);
    const label = this.formData.countryCode === 'US'
      ? this.$t('labels.state') as string
      : this.$t('labels.province') as string;

    return {
      rules: required ? this.requiredRules : [],
      suffix: '',
      label: required ? `${label} *` : label,
    };
  }

  get postalCodeRules(): InputValidationRules {
    if (!this.currentCountry) return this.requiredRules;
    return getPostalCodeRules(this.currentCountry);
  }

  @Watch('addressItem', { immediate: true, deep: true })
  private onAddressItemChange() {
    if (this.addressItem) {
      this.formData = { ...this.addressItem };
    }
  }

  @Watch('currentCountry')
  private validatePostalCodeInput() {
    this.$nextTick(() => {
      if (this.$refs.editForm) {
        (this.$refs.editForm as HTMLFormElement & { validate: () => boolean }).validate();
      }
    });
  }

  private handleClose() {
    this.resetForm();
    this.syncedShow = false;
  }

  private resetForm() {
    if (this.addressItem) {
      this.formData = { ...this.addressItem };
    }
    // Reset form validation
    if (this.$refs.editForm) {
      (this.$refs.editForm as HTMLFormElement & { resetValidation: () => void }).resetValidation();
    }
  }

  private handlePostalCodeBlur(): void {
    this.formData.postalCode = stripUnoCharFromPostalCode(this.formData.postalCode);
    if (this.formData.postalCode) {
      this.formData.postalCode = this.formData.postalCode.toUpperCase();
    }

    const fill = (city: string, province: string, residential: boolean): void => {
      this.formData.city = city || this.formData.city;
      this.formData.province = province || this.formData.province;
      this.formData.residential = residential || this.formData.residential;
    };

    this.autofillCityProvince(fill, 'formData');
  }

  private async updateAddress() {
    if (!this.isFormValid) return;

    const updatedFormData = {
      ...this.formData,
      postalCode: stripUnoCharFromPostalCode(this.formData.postalCode),
    };

    this.isUpdating = true;

    try {
      const response = await updateAddressBookItem(updatedFormData.id, updatedFormData);

      if (response.successful && response.item) {
        this.$emit('updated', response.item);
        showSuccessAlertDialog(this.$t('alertDialog.addressBookUpdated') as string);
        this.syncedShow = false;
      } else if (response.errorMessage) {
        showErrorAlertDialog(response.errorMessage);
      } else {
        showErrorAlertDialog(this.$t('alertDialog.somethingWentWrong') as string);
      }
    } catch (error) {
      showErrorAlertDialog(this.$t('alertDialog.somethingWentWrong') as string);
    }

    this.isUpdating = false;
  }
}
